/**
 * CoverageCalendarView Component
 *
 * Calendar-style view of capacity coverage by role and date
 */

import { useState } from 'react';
import { Box, Typography, Paper, IconButton, Chip, Popover, Divider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CapacitySnapshot, ProviderCapacityDay } from '../types/capacity.types';
import { CoverageMetricType, RoleCapacityDayExtended } from '../types/metrics.types';
import { buildExtendedRoleCapacity } from '../engine/advancedMetrics';
import { formatMinutes } from '../utils/formatters';
import { getCellBackgroundColor, getVisitCapacityBackgroundColor } from '../utils/colorHelpers';
import CapacityDetailsDialog from './CapacityDetailsDialog';

interface CoverageCalendarViewProps {
  snapshot: CapacitySnapshot;
  metricType: CoverageMetricType;
}

export default function CoverageCalendarView({ snapshot, metricType }: CoverageCalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredRoleDay, setHoveredRoleDay] = useState<RoleCapacityDayExtended | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRoleDay, setSelectedRoleDay] = useState<RoleCapacityDayExtended | null>(null);

  // Build extended role capacity with metrics
  const extendedRoleCapacity = buildExtendedRoleCapacity(snapshot.roles, snapshot.providers);

  // Get unique roles
  const uniqueRoles = Array.from(new Set(snapshot.roles.map((r) => r.role))).sort();

  // Hover handlers
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, roleDay: RoleCapacityDayExtended) => {
    setAnchorEl(event.currentTarget);
    setHoveredRoleDay(roleDay);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setHoveredRoleDay(null);
  };

  // Click handlers
  const handleClick = (roleDay: RoleCapacityDayExtended) => {
    setSelectedRoleDay(roleDay);
    setDialogOpen(true);
    handlePopoverClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRoleDay(null);
  };

  const popoverOpen = Boolean(anchorEl);

  // Navigate to previous month
  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Get calendar data for current month
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);

    // Start from Sunday before first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End on Saturday after last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days: Date[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Get role capacity for a specific date
  const getRoleCapacityForDate = (date: Date, role: string): RoleCapacityDayExtended | undefined => {
    const dateKey = date.toISOString().split('T')[0];
    return extendedRoleCapacity.find((r) => r.role === role && r.date === dateKey);
  };

  // Get provider details for a specific role-day
  const getProviderDetailsForRoleDay = (roleDay: RoleCapacityDayExtended): ProviderCapacityDay[] => {
    return snapshot.providers.filter(
      (provider) => provider.role === roleDay.role && provider.date === roleDay.date
    );
  };

  // Format metric value for display
  const getMetricDisplay = (roleDay: RoleCapacityDayExtended | undefined): string => {
    if (!roleDay) return '-';

    switch (metricType) {
      case 'totalCapacity':
        // Show 0 if capacity is 0 or negative, otherwise show the value without + sign
        const capacityHours = Math.max(0, Math.round(roleDay.gapMinutes / 60));
        return `${capacityHours}h`;
      case 'avgContinuousTime':
        return `${Math.round(roleDay.avgContinuousMinutes / 60)}h`;
      case 'visitCapacity':
        return `${roleDay.visitCapacity}`;
      default:
        return '-';
    }
  };

  // Get metric color - use same light colors as heatmap
  const getMetricColor = (roleDay: RoleCapacityDayExtended | undefined): string => {
    if (!roleDay) return '#f5f5f5';

    // Use different color logic based on metric type
    if (metricType === 'visitCapacity') {
      return getVisitCapacityBackgroundColor(roleDay.visitCapacity);
    } else {
      return getCellBackgroundColor(roleDay.surplusPercent);
    }
  };

  const calendarDays = getCalendarDays();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Check if date is today
  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString();
  };

  return (
    <Box>
      {/* Month Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={handlePreviousMonth}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          {monthName}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Calendar Grid */}
      <Paper sx={{ overflow: 'hidden' }}>
        {/* Day Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Box
              key={day}
              sx={{
                p: 1,
                textAlign: 'center',
                bgcolor: '#fafafa',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Calendar Days */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gridAutoRows: 'minmax(120px, auto)',
          }}
        >
          {calendarDays.map((date, index) => {
            const inCurrentMonth = isCurrentMonth(date);
            const isTodayDate = isToday(date);

            return (
              <Box
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  bgcolor: inCurrentMonth ? 'white' : '#fafafa',
                  opacity: inCurrentMonth ? 1 : 0.5,
                  position: 'relative',
                  minHeight: 120,
                }}
              >
                {/* Date Number */}
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    fontWeight={isTodayDate ? 700 : 500}
                    sx={{
                      color: isTodayDate ? 'primary.main' : 'text.primary',
                      fontSize: isTodayDate ? '1rem' : '0.875rem',
                    }}
                  >
                    {date.getDate()}
                  </Typography>
                  {isTodayDate && (
                    <Chip label="Today" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Box>

                {/* Role Capacity Indicators */}
                {inCurrentMonth && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {uniqueRoles.map((role) => {
                      const roleDay = getRoleCapacityForDate(date, role);
                      const metricValue = getMetricDisplay(roleDay);
                      const metricColor = getMetricColor(roleDay);

                      if (!roleDay) return null;

                      return (
                        <Box
                          key={role}
                          onMouseEnter={(e) => handlePopoverOpen(e, roleDay)}
                          onMouseLeave={handlePopoverClose}
                          onClick={() => handleClick(roleDay)}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 0.5,
                            bgcolor: metricColor,
                            borderRadius: 0.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: 1,
                            },
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                            {role}
                          </Typography>
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem' }}>
                            {metricValue}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Hover Popover */}
      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={popoverOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        {hoveredRoleDay && (
          <Box sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="subtitle2" gutterBottom>
              {hoveredRoleDay.role} - {new Date(hoveredRoleDay.date).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Total Shift Time:
              </Typography>
              <Typography variant="body2">
                {formatMinutes(hoveredRoleDay.totalShiftMinutes)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Total Visit Time:
              </Typography>
              <Typography variant="body2">
                {formatMinutes(hoveredRoleDay.totalVisitMinutes)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Available Capacity:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color={hoveredRoleDay.gapMinutes < 0 ? 'error' : 'success.main'}>
                {formatMinutes(Math.max(0, hoveredRoleDay.gapMinutes))}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Visit Capacity:
              </Typography>
              <Typography variant="body2">
                {hoveredRoleDay.visitCapacity} visits
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
              Click for details
            </Typography>
          </Box>
        )}
      </Popover>

      {/* Details Dialog */}
      {selectedRoleDay && (
        <CapacityDetailsDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          roleDay={selectedRoleDay}
          providerDetails={getProviderDetailsForRoleDay(selectedRoleDay)}
          metricType={metricType}
        />
      )}
    </Box>
  );
}
